
import { Vue, Component, Prop } from 'vue-property-decorator'
import InfoBanner from '@/components/processing_steps/banners/InfoBanner.vue'
import axios from 'axios'
import { IBannerConfig, IOrgaInfoBannerResponse } from '@/types/processingSteps'
import { SITE_URLS } from '@/utils/helpers'

@Component({
  components: {
    InfoBanner
  }
})
export default class OrgaInfoBanner extends Vue {
  @Prop({ default: false }) isSiteAdmin!: boolean
  @Prop() view!: 'account_project_list' | 'account_funding_page_list' | 'account_funding_scope_list' | 'account_organization_list' | 'account_project_create' | 'account_funding_application_list'

  orgaInfoBannerData: IOrgaInfoBannerResponse | null = null
  orgaSlug: string | null = null
  showOrgaNotSubmittedBanner = false
  showOrgaHasNoFundingScopesBanner = false
  showOrganizationNotLiveBanner = false
  showOrgaHasNoProjectsBanner = false

  get orgaNotSubmittedText (): string {
    return `
      <p>
        ${this.$gettext('You have not yet submitted your organizational data to Team FörderApp for review. Please submit the data and complete the organization registration in order to continue with the application process.')}
        <br><br>
        <strong>
          ${this.$gettext('Your FörderApp team')}
        </strong>
      </p>
    `
  }

  get orgaHasNoFundingScopesText (): string {
    return `
      <p>
        ${this.$gettext('To create a project, please first create a funding area or complete a funding area that you have already started but not yet filled out completely. Only then can you proceed with the project.')}
        <br><br>
        <strong>
          ${this.$gettext('Your FörderApp team')}
        </strong>
      </p>
    `
  }

  get orgaHasNoProjectsText (): string {
    return `
      <p>
        ${this.$gettext('No projects available. There are currently no projects available for your organization.')}
        <br>
        ${this.$gettext('Please create a project first or contact us at foerderapp@hausdesstiftens.org.')}
        <br><br>
        <strong>
          ${this.$gettext('Your FörderApp team')}
        </strong>
      </p>
    `
  }

  get orgaNotLiveText (): string {
    return `
      <p>
        ${this.$gettext('Your organization is currently being checked by the FörderApp team. This usually takes 2-3 working days. As soon as the check has been approved, you will receive a notification and your projects will be published automatically.')}
        <br><br>
        ${this.$gettext('Thank you for your patience.')}
        <br><br>
        <strong>
          ${this.$gettext('Your FörderApp team')}
        </strong>
      </p>
    `
  }

  get activeBanner (): IBannerConfig {
    if (this.isSiteAdmin) {
      return { show: false, text: '', buttonText: '', action: (): void => { /* no action needed */ } }
    }

    // organization has not submitted its data
    // shouldnt that be displayed in all views?
    if (
      this.showOrgaNotSubmittedBanner &&
      ['account_project_list', 'account_funding_scope_list', 'account_funding_application_list'].includes(this.view)
    ) {
      return {
        show: true,
        text: this.orgaNotSubmittedText,
        buttonText: this.$gettext('Continue registration'),
        action: this.onOrgaNotSubmitted
      }
    }
    // the organization is in review (hds admin checks orga)
    if (
      this.showOrganizationNotLiveBanner &&
      ['account_organization_list', 'account_project_create'].includes(this.view)
    ) {
      return {
        show: true,
        text: this.orgaNotLiveText,
        buttonText: 'Continue registration',
        action: this.closeInfoModal
      }
    }
    if (
      this.showOrgaHasNoFundingScopesBanner &&
      ['account_project_list', 'account_funding_application_list'].includes(this.view)
    ) {
      return {
        show: true,
        text: this.orgaHasNoFundingScopesText,
        buttonText: 'Continue registration',
        action: this.onOrgaHasNoFundingScopes
      }
    }
    if (
      this.showOrgaHasNoProjectsBanner &&
      ['account_project_list', 'account_funding_application_list'].includes(this.view)
    ) {
      return {
        show: true,
        text: this.orgaHasNoProjectsText,
        buttonText: this.$gettext('Create project now'),
        action: this.onOrgaHasNoProjects
      }
    }
    return { show: false, text: '', buttonText: '', action: (): void => { /* no action needed */ } }
  }

  async mounted () {
    await this.fetchOrgaInfo()
  }

  async fetchOrgaInfo () {
    if (this.isSiteAdmin) return
    let url = '/api/v4/processing-steps/show-info-banner/'
    // TODO remove orgaslug
    if (this.orgaSlug) {
      url += `?orga_slug=${this.orgaSlug}/`
    }
    try {
      const response = await axios.get(url)
      const data = response.data as IOrgaInfoBannerResponse
      this.$emit("fetched-orga-info-banner-data", data)
      this.orgaInfoBannerData = data
      this.orgaSlug = data.orga_slug
      this.showOrgaNotSubmittedBanner = data.show_orga_not_submitted_banner
      this.showOrgaHasNoFundingScopesBanner = data.show_orga_has_no_funding_scopes_banner
      this.showOrganizationNotLiveBanner = data.show_organization_not_live_banner
      this.showOrgaHasNoProjectsBanner = data.show_orga_has_no_projects_banner
    } catch {
      // do nothing
    }
  }

  onOrgaHasNoFundingScopes () {
    this.$emit("orga-has-no-funding-scopes", this.orgaInfoBannerData)
    this.closeInfoModal()
    window.location.href = SITE_URLS.ACCOUNT.FUNDING_SCOPE.LIST
  }

  onOrgaHasNoProjects () {
    this.closeInfoModal()
    window.location.href = SITE_URLS.ACCOUNT.PROJECTS.CREATE
  }

  onOrgaNotSubmitted () {
    this.$emit("orga-not-submitted")
    this.closeInfoModal()
    if (this.orgaSlug) {
      window.location.href = SITE_URLS.ACCOUNT.ORGANIZATION.UPDATE(this.orgaSlug)
    }
  }

  closeInfoModal () {
    if (this.showOrgaNotSubmittedBanner) this.showOrgaNotSubmittedBanner = false
    if (this.showOrgaHasNoFundingScopesBanner) this.showOrgaHasNoFundingScopesBanner = false
    if (this.showOrganizationNotLiveBanner) this.showOrganizationNotLiveBanner = false
    if (this.showOrgaHasNoProjectsBanner) this.showOrgaHasNoProjectsBanner = false
    this.$emit('close-info-modal')
  }
}

